import { useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useProjects } from '@/hooks/use-projects';
import { EmptyState } from '@/components/empty-state';
import { createFileRoute } from '@tanstack/react-router';
import { usersOptions } from '@/hooks/query-options';
import { UserItem } from '@/components/user-item';
import { Pagination } from '@/components/pagination';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';

export const Route = createFileRoute('/_auth/_side-layout/users')({
  component: Users,
  loader: ({ context }) => {
    const projectId = context.projectId;
    if (!projectId) {
      return;
    }
    context.queryClient.prefetchQuery(usersOptions({ projectId, page: 1 }));
  },
});

function Users() {
  const { projectId } = useProjects();
  const [page, setPage] = useState<number>(1);
  const { data } = useSuspenseQuery(
    usersOptions({ projectId: projectId!, page }),
  );

  const users = data?.users ?? [];

  if (!users.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          launchState
          headText="We Haven't Seen Any User Activity Yet"
          subText="Add our code to your website to quickly start sending user events."
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Users"
        description="Browse and search users tracked by your project."
        actions={
          <span className="text-primary text-xl font-extrabold md:text-3xl">
            {data?.count}
          </span>
        }
      />
      <PageContent>
        <div className="flex flex-col gap-2">
          {users.map((user, index) => (
            <UserItem key={index} {...user} />
          ))}
        </div>
        <Pagination
          currentPage={page}
          totalPages={Math.ceil((data?.count ?? 0) / 50)}
          onPageChange={setPage}
        />
      </PageContent>
    </>
  );
}
